import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../context";
import Logo from "../../../components/logo";
import LanguageSwitcher from "../../../components/languageSwitcher";
import ThemeToggle from "../../../components/themeToggle";
import { ReactComponent as DownIcon } from "../../../assets/down.svg";
import { Link } from "react-router-dom";
import TopBanner from "../TopBanner";

export default function ProductNavBar() {
  const [activeLink, setActiveLink] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { isDarkMode, shouldDisplayBanner, setShowBanner } = useAppContext();
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const navLinks = [
    { href: "#benefits", label: "Benefits", id: "benefits" },
    { href: "#workflow", label: "Workflow", id: "workflow" },
    { href: "#faqs", label: "Faqs", id: "faqs" },
  ];
  const products = [
    { name: "Social CrowdFund", href: "/products/crowdfund" },
    { name: "Group Saving (IKIMINA)", href: "/products/group-saving" },
    { name: "U-Save", href: "/products/u-save" },
  ];

  const handleLinkClick = (link) => {
    setActiveLink(link);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  return (
    <div className={"fixed top-0 left-0 right-0 z-50"}>
      <TopBanner
        shouldDisplayBanner={shouldDisplayBanner}
        setShowBanner={setShowBanner}
      />
      <nav className="px-6 py-4 flex items-center justify-between bg-[rgb(0,73,69)] backdrop-blur-sm w-full">
        <div className="flex items-center">
          <Logo />
        </div>
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-12 text-white">
          <Link
            to={"/"}
            className={`font-medium hover:text-yellow-400 ${
              activeLink === "home" ? "text-yellow-400" : ""
            }`}
            onClick={() => handleLinkClick("home")}
          >
            {t(`links.Home`)}
          </Link>
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              className={`font-medium hover:text-yellow-400 ${
                activeLink === link.id ? "text-yellow-400" : ""
              }`}
              onClick={() => handleLinkClick(link.id)}
            >
              {t(`links.${link.label}`)}
            </a>
          ))}
          {/* Products Dropdown */}
          <div className="relative group cursor-pointer">
            <span
              className={`font-medium hover:text-yellow-400 inline-flex items-center gap-2`}
              onClick={() => handleLinkClick("products")}
            >
              {t("links.Products")}
              <DownIcon className="w-5 h-5 transition-transform group-hover:rotate-180" />
            </span>
            <div className="w-44 bg-white dark:bg-defaultBlack absolute rounded-md left-0 transition-all duration-300 overflow-hidden h-0 group-hover:h-auto shadow-lg z-50">
              <ul className="font-semibold text-sm">
                {products.map((product) => (
                  <li key={product.name}>
                    <Link
                      to={product.href}
                      className="cursor-pointer p-2 inline-flex items-center w-full gap-2 hover:bg-black/20 dark:hover:bg-white/10 text-black dark:text-white transition-colors"
                    >
                      {product.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* Mobile Navigation Toggle */}
        <div className="md:hidden flex items-center space-x-4">
          <button
            className="text-white focus:outline-none"
            onClick={toggleMenu}
            aria-controls="mobile-menu"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? t("links.closeMenu") : t("links.openMenu")}
          >
            {isMenuOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            )}
          </button>
        </div>
        <div
          ref={menuRef}
          id="mobile-menu"
          className={`md:hidden absolute top-16 left-0 w-full h-screen bg-[rgb(0,73,69)] text-white flex flex-col items-start py-4 space-y-4 z-50 transition-transform duration-300 px-6 ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <Link
            to={"/"}
            className={`font-medium hover:text-yellow-400 ${
              activeLink === "home" ? "text-yellow-400" : ""
            }`}
            onClick={() => handleLinkClick("home")}
          >
            {t(`links.Home`)}
          </Link>
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              className={`font-medium hover:text-yellow-400 ${
                activeLink === link.id ? "text-yellow-400" : ""
              }`}
              onClick={() => handleLinkClick(link.id)}
            >
              {t(`links.${link.label}`)}
            </a>
          ))}
          {/* Mobile Products with Indentation */}
          <div className="flex flex-col space-y-2 w-full">
            <span
              className={`font-medium`}
              onClick={() => handleLinkClick("products")}
            >
              {t("links.Products")}
            </span>
            <div className="flex flex-col space-y-1 ml-3">
              {products.map((product) => (
                <Link
                  key={product.name}
                  to={product.href}
                  className="text-sm opacity-80 hover:text-yellow-400 px-2 py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {product.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-start space-y-4 mt-4">
            <LanguageSwitcher onChangeLanguage={() => setIsMenuOpen(false)} />
            <div className="flex items-center space-x-2">
              <ThemeToggle
                isNewDesign={true}
                onToggleTheme={() => setIsMenuOpen(false)}
              />
              <span className="font-medium">
                {isDarkMode ? t("themes.darkMode") : t("themes.lightMode")}
              </span>
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-4">
          <LanguageSwitcher />
          <ThemeToggle isNewDesign={true} />
        </div>
      </nav>
    </div>
  );
}
